import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Menu, Search, Calendar, User, LogOut, Home, Plus, TrendingUp, MessageCircle, Building, Receipt } from "lucide-react"
import { useAuth } from "@/hooks/useAuth"
import { useProfiles } from "@/hooks/useProfiles"
import { useAdmin } from "@/hooks/useAdmin"
import { useUnreadMessages } from "@/hooks/useUnreadMessages"
import { Link, useLocation } from "react-router-dom"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { EmailVerificationBanner } from "@/components/EmailVerificationBanner"
import { FairStaysLogo } from "@/components/ui/fairstays-logo"
import { useUserMode } from "@/hooks/useUserMode"
import { UserModeSwitch } from "@/components/ui/user-mode-switch"
import { LanguageSwitcher } from "@/components/LanguageSwitcher"
import { cn } from "@/lib/utils"
import { useSwipeGesture, useElementSwipe } from "@/hooks/useSwipeGesture"
import { useIsMobile } from "@/hooks/use-mobile"
import { Badge } from "@/components/ui/badge"
import { useTranslation } from "react-i18next"

const Header = () => {
  const { user, signOut } = useAuth()
  const { profile } = useProfiles()
  const { isAdmin } = useAdmin()
  const { unreadCount } = useUnreadMessages()
  const { mode } = useUserMode()
  const location = useLocation()
  const isMobile = useIsMobile()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const isApprovedHost = user && profile?.host_approved
  const isHostMode = mode === 'host'

  // Swipe gesture to open menu from right edge
  useSwipeGesture({
    onSwipeLeft: () => setMobileMenuOpen(true),
    enabled: isMobile && !mobileMenuOpen,
    edgeThreshold: 30,
    threshold: 50
  })

  // Swipe handlers for closing the menu
  const menuSwipeHandlers = useElementSwipe(
    () => setMobileMenuOpen(false),
    80
  )

  const { t } = useTranslation()
  
  // Standard navigation items - Logo already links to home, no need for redundant "Start" item
  const navItems = [
    { href: "/listings", label: t('common.listings'), icon: Search },
    { href: "/help", label: t('common.help'), icon: null },
  ]

  return (
    <>
      <EmailVerificationBanner />
      {/* Accessible header with proper landmarks */}
      <header 
        className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur-sm"
        role="banner"
      >
        <div className="container mx-auto px-4">
          <div className="flex h-16 items-center justify-between">
            
            {/* Logo with proper accessibility - serves as home link */}
            <Link 
              to="/" 
              className="flex items-center gap-2 hover:opacity-80 transition-opacity mr-6"
              aria-label="Fair-Stays Startseite"
            >
              <FairStaysLogo className="h-7 w-7" />
              <span className="text-xl font-bold tracking-tight">Fair-Stays</span>
            </Link>

            {/* Main Navigation with proper ARIA */}
            <nav 
              className="hidden md:flex items-center"
              role="navigation"
              aria-label="Hauptnavigation"
            >
              <div className="standard-nav">
                {navItems.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={cn(
                      "standard-nav-item",
                      location.pathname === item.href && "active"
                    )}
                    aria-current={location.pathname === item.href ? "page" : undefined}
                  >
                    <span className="flex items-center gap-2">
                      {item.icon && <item.icon className="h-4 w-4" aria-hidden="true" />}
                      {item.label}
                    </span>
                  </Link>
                ))}
              </div>
            </nav>

            {/* Actions with proper labeling */}
            <div className="flex items-center gap-0.5 sm:gap-1 ml-auto" role="group" aria-label="Benutzeraktionen">
              {/* Language Switcher */}
              <LanguageSwitcher />
              {user ? (
                <>
                  {/* User Mode Switch - only for approved hosts */}
                  <UserModeSwitch />
                  
                  {/* Messages Button with Unread Badge */}
                  <Link to="/messages" className="relative">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="hidden sm:flex"
                      aria-label={`Nachrichten${unreadCount > 0 ? ` (${unreadCount} ungelesen)` : ''}`}
                    >
                      <MessageCircle className="h-4 w-4" aria-hidden="true" />
                      {unreadCount > 0 && (
                        <Badge 
                          variant="destructive" 
                          className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
                        >
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </Badge>
                      )}
                    </Button>
                  </Link>
                  
                  <Link to="/bookings">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="hidden sm:flex gap-2"
                      aria-label={t('header.bookings')}
                    >
                      <Calendar className="h-4 w-4" aria-hidden="true" />
                      {t('header.bookings')}
                    </Button>
                  </Link>
                   
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        aria-label={t('header.accountMenu')}
                      >
                        <User className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem asChild>
                        <Link to="/profile" className="flex items-center gap-2 cursor-pointer">
                          <User className="h-4 w-4" />
                          {t('header.profile')}
                        </Link>
                      </DropdownMenuItem>
                      
                      <DropdownMenuItem asChild>
                        <Link to="/messages" className="flex items-center justify-between cursor-pointer">
                          <span className="flex items-center gap-2">
                            <MessageCircle className="h-4 w-4" />
                            {t('header.messages')}
                          </span>
                          {unreadCount > 0 && (
                            <Badge variant="destructive" className="h-5 px-1.5 text-xs">
                              {unreadCount}
                            </Badge>
                          )}
                        </Link>
                      </DropdownMenuItem>
                      
                      {isApprovedHost && (
                        <>
                          <DropdownMenuItem asChild>
                            <Link to="/host-services" className="flex items-center gap-2 cursor-pointer">
                              <Building className="h-4 w-4" />
                              {t('header.hostServices')}
                            </Link>
                          </DropdownMenuItem>
                          
                          <DropdownMenuItem asChild>
                            <Link to="/tax-profile" className="flex items-center gap-2 cursor-pointer">
                              <Receipt className="h-4 w-4" />
                              {t('header.taxProfile')}
                            </Link>
                          </DropdownMenuItem>
                        </>
                      )}
                      
                      {isAdmin() && (
                        <DropdownMenuItem asChild>
                          <Link to="/admin" className="flex items-center gap-2 cursor-pointer">
                            <User className="h-4 w-4" />
                            Admin Panel
                          </Link>
                        </DropdownMenuItem>
                      )}
                      
                      <DropdownMenuSeparator />
                      
                      <DropdownMenuItem onClick={signOut} className="flex items-center gap-2 cursor-pointer">
                        <LogOut className="h-4 w-4" />
                        {t('header.signOut')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <>
                  <Link to="/auth">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="hidden sm:flex"
                      aria-label={t('common.login')}
                    >
                      {t('common.login')}
                    </Button>
                  </Link>
                  <Link to="/auth?mode=signup">
                    <Button 
                      variant="default" 
                      size="sm"
                      aria-label={t('common.register')}
                    >
                      {t('common.register')}
                    </Button>
                  </Link>
                </>
              )}
              
              {/* Mobile menu with accessibility */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="md:hidden"
                    aria-label="Mobilmenü öffnen"
                    aria-expanded={mobileMenuOpen}
                    aria-controls="mobile-menu"
                  >
                    <Menu className="h-5 w-5" aria-hidden="true" />
                  </Button>
                </SheetTrigger>
                <SheetContent 
                  side="right" 
                  className="w-[85vw] sm:w-[350px] !p-0"
                  id="mobile-menu"
                  aria-labelledby="mobile-menu-title"
                  {...menuSwipeHandlers}
                >
                  <SheetHeader className="p-6 pb-4 border-b shrink-0">
                    <SheetTitle id="mobile-menu-title" className="flex items-center gap-2">
                      <FairStaysLogo className="h-5 w-5" />
                      Fair-Stays Navigation
                    </SheetTitle>
                  </SheetHeader>
                  {/* Accessible mobile menu content - scrollable */}
                  <nav 
                    className="space-y-4"
                    role="navigation"
                    aria-label="Mobile Navigation"
                  >
                    {user ? (
                      <>
                         <div className="pb-4 border-b" role="group" aria-labelledby="user-info">
                           <h3 id="user-info" className="sr-only">{t('header.account')}</h3>
                           <p className="font-medium">{user.email}</p>
                           <p className="text-sm text-muted-foreground">
                             {isApprovedHost ? t('header.host') : t('header.guest')}
                           </p>
                           {/* User Mode Switch for mobile */}
                           <div className="mt-3">
                             <UserModeSwitch />
                           </div>
                         </div>
                        
                         <div role="group" aria-labelledby="nav-links">
                          <h3 id="nav-links" className="sr-only">Navigation</h3>
                          {navItems.map((item) => (
                            <Link
                              key={item.href}
                              to={item.href}
                              onClick={() => setMobileMenuOpen(false)}
                              className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent transition-colors"
                              aria-current={location.pathname === item.href ? "page" : undefined}
                            >
                              {item.icon && <item.icon className="h-4 w-4" aria-hidden="true" />}
                              <span>{item.label}</span>
                            </Link>
                          ))}
                        </div>
                        
                        {/* Host-specific menu items */}
                        {isHostMode && isApprovedHost && (
                          <div className="py-2 border-t" role="group" aria-labelledby="host-links">
                            <h3 id="host-links" className="text-xs font-semibold text-muted-foreground px-3 py-2">
                              {t('header.host').toUpperCase()}
                            </h3>
                            <Link
                              to="/my-listings"
                              onClick={() => setMobileMenuOpen(false)}
                              className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent transition-colors"
                              aria-label={t('header.myListings')}
                            >
                              <Home className="h-4 w-4" aria-hidden="true" />
                              <span>{t('header.myListings')}</span>
                            </Link>
                            
                            <Link
                              to="/create-listing"
                              onClick={() => setMobileMenuOpen(false)}
                              className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent transition-colors"
                              aria-label={t('header.newListing')}
                            >
                              <Plus className="h-4 w-4" aria-hidden="true" />
                              <span>{t('header.newListing')}</span>
                            </Link>
                            
                            <Link
                              to="/host-dashboard"
                              onClick={() => setMobileMenuOpen(false)}
                              className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent transition-colors"
                              aria-label={t('header.analytics')}
                            >
                              <TrendingUp className="h-4 w-4" aria-hidden="true" />
                              <span>{t('header.analytics')}</span>
                            </Link>
                            
                            <Link
                              to="/messages"
                              onClick={() => setMobileMenuOpen(false)}
                              className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent transition-colors"
                              aria-label={t('header.messages')}
                            >
                              <MessageCircle className="h-4 w-4" aria-hidden="true" />
                              <span>{t('header.messages')}</span>
                            </Link>
                          </div>
                        )}
                        
                        <div className="py-2 border-t" role="group" aria-labelledby="account-links">
                          <h3 id="account-links" className="text-xs font-semibold text-muted-foreground px-3 py-2">
                            {t('header.account').toUpperCase()}
                          </h3>
                          
                          {/* Messages link for all users */}
                          <Link
                            to="/messages"
                            onClick={() => setMobileMenuOpen(false)}
                            className="flex items-center justify-between p-3 rounded-lg hover:bg-accent transition-colors"
                            aria-label={t('header.messages')}
                          >
                            <span className="flex items-center gap-3">
                              <MessageCircle className="h-4 w-4" aria-hidden="true" />
                              <span>{t('header.messages')}</span>
                            </span>
                            {unreadCount > 0 && (
                              <Badge variant="destructive" className="h-5 px-1.5 text-xs">
                                {unreadCount}
                              </Badge>
                            )}
                          </Link>
                          
                          <Link
                            to="/bookings"
                            onClick={() => setMobileMenuOpen(false)}
                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent transition-colors"
                            aria-label={t('header.bookings')}
                          >
                            <Calendar className="h-4 w-4" aria-hidden="true" />
                            <span>{t('header.bookings')}</span>
                          </Link>
                        
                          <Link
                            to="/profile"
                            onClick={() => setMobileMenuOpen(false)}
                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent transition-colors"
                            aria-label={t('header.profile')}
                          >
                            <User className="h-4 w-4" aria-hidden="true" />
                            <span>{t('header.profile')}</span>
                          </Link>
                          
                          {isApprovedHost && (
                            <>
                              <Link
                                to="/host-services"
                                onClick={() => setMobileMenuOpen(false)}
                                className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent transition-colors"
                                aria-label={t('header.hostServices')}
                              >
                                <Building className="h-4 w-4" aria-hidden="true" />
                                <span>{t('header.hostServices')}</span>
                              </Link>
                              
                              <Link
                                to="/tax-profile"
                                onClick={() => setMobileMenuOpen(false)}
                                className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent transition-colors"
                                aria-label={t('header.taxProfile')}
                              >
                                <Receipt className="h-4 w-4" aria-hidden="true" />
                                <span>{t('header.taxProfile')}</span>
                              </Link>
                            </>
                          )}
                          
                          {isAdmin() && (
                            <Link
                              to="/admin"
                              onClick={() => setMobileMenuOpen(false)}
                              className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent transition-colors"
                              aria-label="Admin Panel"
                            >
                              <User className="h-4 w-4" aria-hidden="true" />
                              <span>Admin Panel</span>
                            </Link>
                          )}
                          
                          <Button
                            onClick={() => {
                              signOut();
                              setMobileMenuOpen(false);
                            }}
                            variant="ghost"
                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent transition-colors w-full justify-start"
                            aria-label={t('header.signOut')}
                          >
                            <LogOut className="h-4 w-4" aria-hidden="true" />
                            <span>{t('header.signOut')}</span>
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        {navItems.map((item) => (
                          <Link
                            key={item.href}
                            to={item.href}
                            onClick={() => setMobileMenuOpen(false)}
                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent transition-colors"
                            aria-current={location.pathname === item.href ? "page" : undefined}
                          >
                            {item.icon && <item.icon className="h-4 w-4" aria-hidden="true" />}
                            <span>{item.label}</span>
                          </Link>
                        ))}
                        
                        <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                          <Button 
                            variant="outline" 
                            className="w-full"
                            aria-label={t('common.login')}
                          >
                            {t('common.login')}
                          </Button>
                        </Link>
                        
                        <Link to="/auth?mode=signup" onClick={() => setMobileMenuOpen(false)}>
                          <Button 
                            variant="default" 
                            className="w-full"
                            aria-label={t('common.register')}
                          >
                            {t('common.register')}
                          </Button>
                        </Link>
                      </>
                    )}
                  </nav>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>
    </>
  )
}

export default Header